import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, TrendingUp, Building2, Stethoscope, PieChart, Users, LogOut } from "lucide-react";
import { Header } from "@/components/Header";
import { KpiGrid } from "@/components/KpiGrid";
import { DoctorTabs } from "@/components/DoctorTabs";
import { AgendaTable } from "@/components/AgendaTable";
import { PaymentDrawer } from "@/components/PaymentDrawer";
import { useClinic } from "@/store/ClinicContext";
import { useAuth } from "@/store/AuthContext";
import { generatePaymentReceipt } from "@/utils/receipt";
import { NotificationsBell } from "@/components/NotificationsBell";
import { DOCTORS, formatBRL } from "@/data/mockData";

export default function Dashboard() {
  const { appointments, advanceStatus, confirmPayment } = useClinic();
  const { logout, currentUser } = useAuth();
  const canSocio = currentUser?.role === "admin";
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("all");
  const [persona, setPersona] = useState("secretaria");
  const [paymentApt, setPaymentApt] = useState(null);

  const handleConfirmPayment = (id, payload) => {
    confirmPayment(id, payload);
    const apt = appointments.find((a) => a.id === id);
    const doctor = DOCTORS.find((d) => d.id === apt?.doctorId);
    generatePaymentReceipt({
      patient: apt?.patient,
      procedure: apt?.procedure,
      doctor: doctor?.name,
      method: payload.method,
      received: payload.received,
      repasseClinica: payload.repasseClinica,
      repasseMedico: payload.repasseMedico,
    });
    setPaymentApt(null);
    toast.success("Caixa atualizado com sucesso!", {
      description: `${formatBRL(payload.received)} via ${payload.method} · Recibo PDF gerado`,
    });
  };

  const counts = useMemo(() => {
    const c = { all: appointments.length };
    DOCTORS.forEach((d) => {
      c[d.id] = appointments.filter((a) => a.doctorId === d.id).length;
    });
    return c;
  }, [appointments]);

  const filtered = useMemo(
    () =>
      activeTab === "all"
        ? appointments
        : appointments.filter((a) => a.doctorId === activeTab),
    [appointments, activeTab]
  );

  const stats = useMemo(() => {
    const total = appointments.length;
    const confirmed = appointments.filter((a) => a.status === "confirmado").length;
    const waiting = appointments.filter((a) => a.status === "na_recepcao").length;
    const attended = appointments.filter((a) => a.status === "atendido").length;
    const paid = appointments.filter((a) => a.paid);
    const revenue = paid.reduce((sum, a) => sum + (a.received ?? 0), 0);
    const confirmRate = total ? Math.round((confirmed / total) * 100) : 0;
    return {
      total,
      confirmed,
      waiting,
      revenue,
      paidCount: paid.length,
      confirmRate,
      remaining: total - attended,
    };
  }, [appointments]);

  const socioStats = useMemo(() => {
    const paid = appointments.filter((a) => a.paid);
    let clinica = 0;
    let medico = 0;
    const byDoctor = {};
    paid.forEach((a) => {
      const c = ((a.received ?? 0) * (a.splitClinica ?? 70)) / 100;
      const m = (a.received ?? 0) - c;
      clinica += c;
      medico += m;
      byDoctor[a.doctorId] = (byDoctor[a.doctorId] ?? 0) + m;
    });
    return { clinica, medico, byDoctor };
  }, [appointments]);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header persona={persona} setPersona={(id) => setPersona(canSocio ? id : "secretaria")} canSocio={canSocio} />

      <main className="mx-auto max-w-[1400px] px-5 py-6 md:px-8 md:py-8">
        {/* Title */}
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                Fluxo do Dia
              </h1>
              <span className="flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">
                <Sparkles className="h-3 w-3" />
                {persona === "socio" ? "Visão do Sócio" : "Visão da Secretária"}
              </span>
            </div>
            <p className="text-sm text-slate-500">
              Acompanhe agendamentos, check-ins e caixa em tempo real.
            </p>
          </div>
          <div className="flex items-center gap-2 self-start sm:self-auto">
            <Link
              to="/medicos"
              data-testid="open-medicos"
              className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition-colors duration-200 hover:bg-slate-50"
            >
              <Users className="h-4 w-4 text-blue-600" />
              Corpo Clínico
            </Link>
            <Link
              to="/diagnostico"
              data-testid="open-diagnostico"
              className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition-colors duration-200 hover:bg-slate-50"
            >
              <Sparkles className="h-4 w-4 text-blue-600" />
              Diagnóstico IA
            </Link>
            <Link
              to="/financeiro"
              data-testid="open-financeiro"
              className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition-colors duration-200 hover:bg-slate-50"
            >
              <PieChart className="h-4 w-4 text-blue-600" />
              CFO de Bolso
            </Link>
            <Link
              to="/atendimento"
              data-testid="open-atendimento"
              className="flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors duration-200 hover:bg-slate-800"
            >
              <Stethoscope className="h-4 w-4" />
              Módulo do Médico
            </Link>
            <button
              data-testid="logout-btn"
              onClick={() => {
                logout();
                navigate("/login", { replace: true });
              }}
              className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 shadow-sm transition-colors duration-200 hover:bg-rose-50 hover:text-rose-600"
            >
              <LogOut className="h-4 w-4" />
              Sair
            </button>
            <NotificationsBell />
          </div>
        </div>

        {/* KPIs */}
        <KpiGrid stats={stats} />

        {/* Sócio financial panel */}
        {persona === "socio" && canSocio && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
            data-testid="socio-panel"
          >
            <div className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
              <div className="mb-2 flex items-center gap-2">
                <Building2 className="h-4 w-4 text-blue-600" />
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Repasse Clínica
                </span>
              </div>
              <p className="font-mono-num text-xl font-bold text-slate-900">
                {formatBRL(socioStats.clinica)}
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
              <div className="mb-2 flex items-center gap-2">
                <Stethoscope className="h-4 w-4 text-emerald-600" />
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Repasse Médicos
                </span>
              </div>
              <p className="font-mono-num text-xl font-bold text-slate-900">
                {formatBRL(socioStats.medico)}
              </p>
            </div>
            {DOCTORS.map((d) => (
              <div
                key={d.id}
                className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
              >
                <div className="mb-2 flex items-center gap-2">
                  <img src={d.avatar} alt={d.name} className="h-5 w-5 rounded-full object-cover" />
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                    {d.shortName}
                  </span>
                </div>
                <p className="font-mono-num text-xl font-bold text-slate-900">
                  {formatBRL(socioStats.byDoctor[d.id] ?? 0)}
                </p>
              </div>
            ))}
          </motion.div>
        )}

        {/* Filters */}
        <div className="mt-8 mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <DoctorTabs active={activeTab} setActive={setActiveTab} counts={counts} />
          <div className="flex items-center gap-2 rounded-full border border-emerald-200/60 bg-emerald-50 px-3 py-1.5">
            <TrendingUp className="h-3.5 w-3.5 text-emerald-600" />
            <span className="text-xs font-semibold text-emerald-700">
              {stats.confirmRate}% confirmadas pela IA
            </span>
          </div>
        </div>

        {/* Agenda */}
        <AgendaTable
          appointments={filtered}
          onAdvanceStatus={advanceStatus}
          onOpenPayment={setPaymentApt}
        />
      </main>

      <PaymentDrawer
        appointment={paymentApt}
        onClose={() => setPaymentApt(null)}
        onConfirm={handleConfirmPayment}
      />
    </div>
  );
}
